import React, { useEffect, useState } from "react";
import logo from "./images/SandCherry_CatalizeResults.png";
import cover from "./images/sc_cover.jpg";

const TOKEN_KEY = "scDirectoryToken";
const LOGOUT_EVENT = "scDirectoryLogout";

function isTokenStillValid(token) {
  try {
    if (!token) return false;

    const parts = token.split(".");
    if (parts.length !== 3) return false;

    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));

    if (!payload.exp) return false;

    return Date.now() < payload.exp;
  } catch {
    return false;
  }
}

function getInitialAllowed() {
  const token = localStorage.getItem(TOKEN_KEY);

  if (!isTokenStillValid(token)) {
    localStorage.removeItem(TOKEN_KEY);
    return false;
  }

  return true;
}

export default function LoginGate({ children }) {
  const [isAllowed, setIsAllowed] = useState(getInitialAllowed);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [email, setEmail] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [toast, setToast] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  function showToast(message, type = "error") {
    setToast({ message, type });

    window.clearTimeout(window.__scToastTimer);

    window.__scToastTimer = window.setTimeout(() => {
      setToast(null);
    }, 3500);
  }

  useEffect(() => {
    function handleLogout(event) {
      localStorage.removeItem(TOKEN_KEY);
      setIsAllowed(false);
      setIsFadingOut(false);

      const message =
        event?.detail?.message || "Please log in again to continue.";

      showToast(message, "error");
    }

    window.addEventListener(LOGOUT_EVENT, handleLogout);

    return () => {
      window.removeEventListener(LOGOUT_EVENT, handleLogout);
    };
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();

    setToast(null);
    setIsLoading(true);

    try {
      const response = await fetch("/api/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email.trim(),
          zipCode: zipCode.trim(),
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success || !data.token) {
        const message = data?.message || "Incorrect email or ZIP code.";
        showToast(message, "error");
        setIsLoading(false);
        return;
      }

      localStorage.setItem(TOKEN_KEY, data.token);

      setIsFadingOut(true);

      window.setTimeout(() => {
        setIsAllowed(true);
      }, 250);
    } catch {
      showToast("Unable to validate login right now. Please try again.", "error");
    } finally {
      setIsLoading(false);
    }
  }

  if (isAllowed) {
    return <div className="animate-[fadeIn_0.35s_ease-in-out]">{children}</div>;
  }

  return (
    <div className="min-h-screen relative overflow-hidden bg-slate-950">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-75"
        style={{ backgroundImage: `url(${cover})` }}
      />

      <div className="absolute inset-0 bg-gradient-to-br from-black/45 via-slate-950/35 to-black/35" />

      {toast && (
        <div className="fixed top-5 left-1/2 z-50 -translate-x-1/2 animate-[toastIn_0.2s_ease-out]">
          <div className="rounded-xl bg-white shadow-2xl border border-red-200 px-5 py-3 text-sm font-medium text-red-700">
            {toast.message}
          </div>
        </div>
      )}

      <div
        className={`relative z-10 min-h-screen flex items-center justify-center px-4 transition-opacity duration-300 ${
          isFadingOut ? "opacity-0" : "opacity-100"
        }`}
      >
        <div className="w-full max-w-md">
          <div className="bg-white/95 backdrop-blur rounded-2xl shadow-2xl border border-white/40 overflow-hidden">
            <div className="bg-black px-8 py-6 flex justify-center">
              <img
                src={logo}
                alt="Sand Cherry"
                className="h-20 object-contain"
              />
            </div>

            <div className="px-8 py-8">
              <h1 className="text-2xl font-semibold text-gray-900 text-center">
                Sand Cherry App
              </h1>

              <p className="text-gray-600 text-center mt-2 mb-7">
                Enter your Sand Cherry email and ZIP code to continue.
              </p>

              <form onSubmit={handleSubmit} className="space-y-5" autoComplete="on">
                <div>
                  <label htmlFor="sca-login-email" className="block text-sm font-medium text-gray-700 mb-1">
                    Sand Cherry Email
                  </label>
                  <input
                    id="sca-login-email"
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    autoCapitalize="none"
                    spellCheck="false"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#9d4a3f] focus:border-[#9d4a3f]"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="sca-login-zip" className="block text-sm font-medium text-gray-700 mb-1">
                    ZIP Code
                  </label>
                  <input
                    id="sca-login-zip"
                    type="text"
                    name="access-zip-code"
                    value={zipCode}
                    onChange={(e) => setZipCode(e.target.value)}
                    autoComplete="off"
                    data-1p-ignore="true"
                    data-lpignore="true"
                    inputMode="text"
                    maxLength={20}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#9d4a3f] focus:border-[#9d4a3f]"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full rounded-lg bg-[#9d4a3f] hover:bg-[#843d34] text-white font-semibold py-2.5 transition disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isLoading ? "Checking..." : "Continue"}
                </button>
              </form>

              <p className="text-xs text-gray-500 text-center mt-6">
                Access is limited to active Sand Cherry consultants.
              </p>
            </div>
          </div>
        </div>
      </div>

      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(4px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes toastIn {
            from {
              opacity: 0;
              transform: translate(-50%, -8px);
            }
            to {
              opacity: 1;
              transform: translate(-50%, 0);
            }
          }
        `}
      </style>
    </div>
  );
}
